import React, { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, TriangleAlert } from 'lucide-react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import {
  STATUS_OPTIONS,
  findWebhookFor,
  getStatusLabel,
  getSubstatusLabel,
  getSubstatuses,
} from '../../../../../features/webhooks/webhooksUtils';

/**
 * Picks the status and substatus a new webhook listens to. Nothing is saved
 * here: the webhook fires as soon as it exists, so it is only created from its
 * detail page, once it has a destination.
 *
 * A pair that already has a webhook cannot continue — a second one would stop
 * both from being sent — and the dialog offers the existing one instead.
 */
const AddWebhookModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const webhooks = useSelector((state) => state.webhooks.webhooks);

  const [status, setStatus] = useState('');
  const [substatus, setSubstatus] = useState('');

  // Every opening starts empty.
  useEffect(() => {
    if (!isOpen) {
      setStatus('');
      setSubstatus('');
    }
  }, [isOpen]);

  const substatusOptions = getSubstatuses(status).map((item) => ({
    value: item.system_name,
    label: item.user_name,
  }));

  const existing = status && substatus ? findWebhookFor(webhooks, status, substatus) : null;
  const canContinue = Boolean(status && substatus && !existing);

  const handleStatusChange = (value) => {
    setStatus(value);
    // Cleared so a substatus of the previous status cannot travel with this one.
    setSubstatus('');
  };

  const goTo = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Agregar webhook" testId="webhook-add-modal">
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        Elige el estado que dispara el aviso. Se envía solo cuando el envío queda exactamente en
        ese estado y subestado.
      </p>

      <div className="space-y-4">
        <FilterableSelect
          variant="form"
          testId="webhook-status"
          label="Estado"
          value={status}
          onChange={handleStatusChange}
          options={STATUS_OPTIONS}
          placeholder="Seleccionar..."
          searchPlaceholder="Buscar estado..."
        />
        <FilterableSelect
          variant="form"
          testId="webhook-substatus"
          label="Subestado"
          value={substatus}
          onChange={setSubstatus}
          options={substatusOptions}
          disabled={!status}
          placeholder={status ? 'Seleccionar...' : 'Elige primero un estado'}
          searchPlaceholder="Buscar subestado..."
        />

        {existing && (
          <div
            data-testid="webhook-duplicate"
            className="flex flex-col sm:flex-row sm:items-center gap-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 rounded-xl p-3"
          >
            <div className="flex items-start gap-2 text-sm text-ninja-dark dark:text-white flex-1">
              <TriangleAlert size={16} className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <span>
                Ya tienes un webhook para{' '}
                <strong>
                  {getStatusLabel(status)} / {getSubstatusLabel(status, substatus)}
                </strong>
                .
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goTo(`/configuration/webhooks/${existing.id}`)}
            >
              Ver webhook
            </Button>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          iconRight={ArrowRight}
          disabled={!canContinue}
          onClick={() =>
            goTo(`/configuration/webhooks/new?${new URLSearchParams({ status, substatus })}`)
          }
        >
          Continuar
        </Button>
      </div>
    </Modal>
  );
};

export default memo(AddWebhookModal);
